import React, { useState } from 'react';
import styled from 'styled-components';
import Modal from 'components/UI/Modal';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { Border, Main, Text } from 'styles/themeColors';
import { FontWeight } from 'lib/styles';
import { TEXT_LABELS } from 'constants/order';
import Warning from '@mui/icons-material/Warning';
import { useAPI } from 'lib/API';
import { cancelConsignment } from 'requests/consignment';
import { renderToast, toast } from 'components/UI/ToastNotification';

const WarningBox = styled(FlexboxContainer)`
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 16px;
`;

const WarningText = styled.span`
  font-size: 14px;
  color: ${Text.primary};
  white-space: pre-line;
`;

const LabelText = styled.span`
  display: block;
  font-weight: ${FontWeight.medium};
  text-align: left;
  margin-bottom: 5px;
  font-size: 15px;
  color: ${Text.primary};
`;

const TextArea = styled.textarea`
  border: 1px solid ${Border.main};
  border-radius: 6px;
  padding: 7px;
  color: ${Text.primary};
  resize: vertical;
`;

export interface Props {
  show: boolean;
  onClose: () => void;
  consignmentId?: string;
  disabled?: boolean;
  onSave?: () => void;
}

const SubscriptionCancelModal = ({
  show,
  onClose,
  consignmentId,
  disabled = false,
  onSave,
}: Props) => {
  const [reason, setReason] = useState('');

  const cancelReq = useAPI({
    deferFn: cancelConsignment,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'Subscription cancellation scheduled successfully');
      onClose();
      onSave?.();
    },
    onReject: (err: any) => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const handleConfirm = () => {
    cancelReq.run(consignmentId, reason);
  };

  return (
    <Modal
      showButtons
      showModal={show}
      title={TEXT_LABELS.CANCEL_SUBSCRIPTION_TITLE}
      onClose={handleClose}
      onClick={handleConfirm}
      disabled={disabled || !reason.trim() || cancelReq.isLoading}
      ghostButtonText={TEXT_LABELS.SUBSCRIPTION_MODAL_CLOSE}
      primaryButtonText={TEXT_LABELS.CANCEL_SUBSCRIPTION_CONFIRM}
      width="560px"
      primaryButtonColor={Main.error}
      color={Text.primary}
      borderColor={Text.primary}
    >
      <FlexboxContainer flexDirection="column">
        <WarningBox flexDirection="row" alignItems="flex-start" gap="8px">
          <Warning style={{ fontSize: 20, marginTop: 22, flexShrink: 0 }} />
          <WarningText>{TEXT_LABELS.CANCEL_SUBSCRIPTION_WARNING}</WarningText>
        </WarningBox>
        <LabelText>{TEXT_LABELS.CANCEL_SUBSCRIPTION_REASON}</LabelText>
        <TextArea
          maxLength={500}
          rows={5}
          value={reason}
          placeholder="Reason"
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
            }
          }}
        />
      </FlexboxContainer>
    </Modal>
  );
};

export default SubscriptionCancelModal;